/**
 * What a person was expected to work, and what counts as worked.
 *
 * This is the arithmetic behind every overtime figure in the app, and the part of
 * the legacy system that was hardest to read: the same absence changes the numbers
 * in two completely different ways depending on one per-contract setting.
 *
 * Ported deliberately, because getting it wrong is invisible - the totals still
 * look plausible, they are just not the ones payroll agreed.
 */
import { addDays, type DateOnly, parseDateOnly } from './time';

/** Seconds per weekday. Keyed, not positional: an array indexed 0=Monday is
 *  exactly the kind of thing that gets read as 0=Sunday once and never noticed. */
export interface HoursPerDay {
  mon: number;
  tue: number;
  wed: number;
  thu: number;
  fri: number;
  sat: number;
  sun: number;
}

export const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type WeekdayKey = (typeof WEEKDAY_KEYS)[number];

/**
 * How an absence affects the day.
 *
 * COMPENSATE - the absence adds to time WORKED, filling the day's expected hours.
 *   The person shows as having met their target; expected time is untouched. All 80
 *   live contracts use this.
 *
 * REDUCE - the absence lowers the EXPECTED hours instead, leaving worked time
 *   alone. The day expects nothing, so an empty day is not a shortfall.
 *
 * The end result for a single ordinary day is the same balance either way, which is
 * why the difference is easy to miss. It stops being the same the moment a day is
 * partly worked: under COMPENSATE a half-day absence plus four hours worked reads
 * as a full day, while under REDUCE it reads as four hours worked against four
 * expected - and any hours beyond that become overtime.
 */
export type AbsenceCalculationMode = 'COMPENSATE' | 'REDUCE';

/** The weekday key for a calendar date. */
export function weekdayKeyOf(day: DateOnly): WeekdayKey {
  // getUTCDay(): 0 = Sunday. Shifted so Monday is index 0, matching WEEKDAY_KEYS.
  const index = (parseDateOnly(day).getUTCDay() + 6) % 7;
  return WEEKDAY_KEYS[index]!;
}

export interface DayContext {
  day: DateOnly;
  /** The contract's expected seconds for this weekday. */
  contractSeconds: number;
  /** Seconds actually recorded against this day. */
  recordedSeconds: number;
  /** A mandatory public holiday falling on this day, if any. */
  holiday?: { mandatory: boolean; halfDay: boolean };
  /** An APPROVED absence covering this day, if any. */
  absence?: { halfDay: boolean };
  mode: AbsenceCalculationMode;
}

export interface DayResult {
  day: DateOnly;
  /** Hours the person was expected to work, after holidays and REDUCE absences. */
  expectedSeconds: number;
  /** Hours counted as worked, including COMPENSATE absence credit. */
  actualSeconds: number;
  /** actual − expected. Positive is overtime, negative is a shortfall. */
  balanceSeconds: number;
  /** Why the expected figure is not simply the contract's, when it is not. */
  note: 'holiday' | 'half-day holiday' | 'absence' | 'half-day absence' | null;
}

/**
 * Resolve one day.
 *
 * Order matters and is not arbitrary. A public holiday is settled FIRST, because it
 * applies to everyone regardless of contract or absence: nobody is expected to work
 * a mandatory holiday, so there is no shortfall to compensate and no expectation
 * left to reduce. Applying an absence first would credit somebody eight hours of
 * leave for a day they were never expected to work, and silently spend a day of
 * their entitlement on it.
 *
 * An OPTIONAL holiday is not a holiday for this purpose - it is a normal working
 * day somebody may choose to take as leave, which is what the flag means.
 */
export function resolveDay(ctx: DayContext): DayResult {
  const { day, contractSeconds, recordedSeconds, holiday, absence, mode } = ctx;

  // A non-working day under the contract (weekend, or a contracted day off) is
  // already zero-expected; nothing below can change that.
  if (contractSeconds === 0) {
    return {
      day,
      expectedSeconds: 0,
      actualSeconds: recordedSeconds,
      balanceSeconds: recordedSeconds,
      note: null,
    };
  }

  if (holiday?.mandatory) {
    const expected = holiday.halfDay ? Math.round(contractSeconds / 2) : 0;
    return {
      day,
      expectedSeconds: expected,
      actualSeconds: recordedSeconds,
      balanceSeconds: recordedSeconds - expected,
      note: holiday.halfDay ? 'half-day holiday' : 'holiday',
    };
  }

  if (absence) {
    const credited = absence.halfDay ? Math.round(contractSeconds / 2) : contractSeconds;
    const note = absence.halfDay ? ('half-day absence' as const) : ('absence' as const);

    if (mode === 'COMPENSATE') {
      // The absence counts as time worked; the day still expects its full hours.
      const actual = recordedSeconds + credited;
      return {
        day,
        expectedSeconds: contractSeconds,
        actualSeconds: actual,
        balanceSeconds: actual - contractSeconds,
        note,
      };
    }

    // REDUCE: the day expects less, and worked time is left exactly as recorded.
    const expected = contractSeconds - credited;
    return {
      day,
      expectedSeconds: expected,
      actualSeconds: recordedSeconds,
      balanceSeconds: recordedSeconds - expected,
      note,
    };
  }

  return {
    day,
    expectedSeconds: contractSeconds,
    actualSeconds: recordedSeconds,
    balanceSeconds: recordedSeconds - contractSeconds,
    note: null,
  };
}

export interface PeriodTotals {
  expectedSeconds: number;
  actualSeconds: number;
  balanceSeconds: number;
  /**
   * The same three figures counting only days up to and including `asOf`.
   *
   * Mid-month, the full-month target against work so far reads as an enormous
   * shortfall - 168 hours expected against 10 recorded on the 3rd, a balance of
   * -157:30 - which is arithmetically right and completely useless. The figure
   * somebody actually wants is "am I behind so far", so both are reported and the
   * screen leads with this one.
   *
   * Null when the period is entirely in the past, where "to date" and the whole
   * period are the same thing.
   */
  toDate: { expectedSeconds: number; actualSeconds: number; balanceSeconds: number } | null;
  /** Working days in the period, holidays and full absences excluded. */
  workingDays: number;
  /** Days carrying an approved absence, halves counted as 0.5. */
  absenceDays: number;
  holidayDays: number;
}

/**
 * Sum a run of days.
 *
 * The totals are summed from the per-day results rather than recomputed from the
 * period, so a month total can never disagree with the days shown under it - which
 * is the bug people actually notice and lose trust over.
 */
export function totalsFor(
  days: DayResult[],
  contexts: DayContext[],
  /** Today, for splitting elapsed days from days that have not happened. */
  asOf?: DateOnly,
): PeriodTotals {
  const byDay = new Map(contexts.map((c) => [c.day, c]));

  let absenceDays = 0;
  let holidayDays = 0;
  let workingDays = 0;

  for (const result of days) {
    const ctx = byDay.get(result.day);
    if (!ctx) continue;

    // Counted only where the contract expected work. A public holiday on a Saturday
    // is not a day off anybody gained - Independence Day 2026 falls on one, and
    // counting it made August report two holidays while showing one. The same
    // applies to an absence spanning a weekend: those days are not leave, which is
    // the rule absenceWorkingDays already applies when the request is made.
    const wasWorkingDay = ctx.contractSeconds > 0;

    if (wasWorkingDay) {
      if (ctx.holiday?.mandatory) {
        holidayDays += ctx.holiday.halfDay ? 0.5 : 1;
      } else if (ctx.absence) {
        absenceDays += ctx.absence.halfDay ? 0.5 : 1;
      }
    }
    if (result.expectedSeconds > 0) workingDays += 1;
  }

  const elapsed = asOf === undefined ? null : days.filter((d) => d.day <= asOf);
  // Only meaningful when the period straddles today: if every day has passed, the
  // two sets of figures are identical and offering both invites the question of
  // why they differ.
  const toDate =
    elapsed === null || elapsed.length === days.length
      ? null
      : {
          expectedSeconds: elapsed.reduce((sum, d) => sum + d.expectedSeconds, 0),
          actualSeconds: elapsed.reduce((sum, d) => sum + d.actualSeconds, 0),
          balanceSeconds: elapsed.reduce((sum, d) => sum + d.balanceSeconds, 0),
        };

  return {
    expectedSeconds: days.reduce((sum, d) => sum + d.expectedSeconds, 0),
    actualSeconds: days.reduce((sum, d) => sum + d.actualSeconds, 0),
    balanceSeconds: days.reduce((sum, d) => sum + d.balanceSeconds, 0),
    toDate,
    workingDays,
    absenceDays,
    holidayDays,
  };
}

/**
 * Working days an absence request actually consumes.
 *
 * Weekends and mandatory public holidays are not leave: somebody taking the week of
 * Diwali off spends four days of entitlement, not five. Counted here rather than
 * left to whoever creates the request, so the number on the request and the number
 * deducted from the balance cannot drift apart.
 */
export function absenceWorkingDays(
  from: DateOnly,
  to: DateOnly,
  hours: HoursPerDay,
  holidays: ReadonlySet<DateOnly>,
  halfDay: boolean,
): number {
  let days = 0;
  let cursor = from;
  while (cursor <= to) {
    if (hours[weekdayKeyOf(cursor)] > 0 && !holidays.has(cursor)) days += 1;
    const next = parseDateOnly(cursor);
    next.setUTCDate(next.getUTCDate() + 1);
    cursor = next.toISOString().slice(0, 10);
  }
  // A half day is only meaningful for a single-day request; a half-week of leave is
  // not a thing the request form can express, so the flag is ignored beyond one day.
  if (halfDay && days === 1) return 0.5;
  return days;
}

/** Total contracted seconds in a week - the expected figure before adjustments. */
export function weeklyContractSeconds(hours: HoursPerDay): number {
  return WEEKDAY_KEYS.reduce((sum, key) => sum + (hours[key] || 0), 0);
}

/**
 * What one week actually expected, after holidays and the employment window.
 *
 * NOT weeklyContractSeconds. That sums the contract and stops, which overstates
 * every week containing a public holiday - eleven of the twenty-seven weeks the
 * previous approach ever snapshotted are holiday weeks, and all eleven recorded a
 * full 40 hours against an actual target of 32.
 *
 * Here rather than in a service because two callers need the identical answer: a
 * week being submitted, and the backfill that repairs the weeks submitted before
 * this existed. A week's target computed two ways is a week whose target is
 * wrong on one screen.
 *
 * Absences are deliberately absent from the signature. Every live contract is
 * COMPENSATE, where an absence adds to time WORKED rather than lowering what was
 * expected, so an absence cannot change this figure - and passing them would
 * invite exactly that double-count.
 */
export function weekExpectedSeconds(
  weekStart: DateOnly,
  hours: HoursPerDay,
  employment: { from: DateOnly | null; to: DateOnly | null },
  /** Mandatory holidays falling in this week, keyed by day. */
  holidays: ReadonlyMap<DateOnly, { mandatory: boolean; halfDay: boolean }>,
  mode: AbsenceCalculationMode,
): number {
  return periodExpectedSeconds(weekStart, addDays(weekStart, 6), hours, employment, holidays, mode);
}

/**
 * The same target for any run of days - one part of a week split at a month end.
 *
 * weekExpectedSeconds is this over seven days, so the two parts of a split week
 * always add up to the week's own target.
 */
export function periodExpectedSeconds(
  from: DateOnly,
  to: DateOnly,
  hours: HoursPerDay,
  employment: { from: DateOnly | null; to: DateOnly | null },
  holidays: ReadonlyMap<DateOnly, { mandatory: boolean; halfDay: boolean }>,
  mode: AbsenceCalculationMode,
): number {
  let expected = 0;
  for (let day = from; day <= to; day = addDays(day, 1)) {
    const employed =
      (employment.from === null || day >= employment.from) &&
      (employment.to === null || day <= employment.to);
    const holiday = holidays.get(day);
    expected += resolveDay({
      day,
      contractSeconds: employed ? hours[weekdayKeyOf(day)] : 0,
      recordedSeconds: 0,
      ...(holiday ? { holiday } : {}),
      mode,
    }).expectedSeconds;
  }
  return expected;
}

/**
 * Read a stored `hoursPerDay` value defensively.
 *
 * The column is JSON, so nothing in the database guarantees its shape, and a
 * contract saved by an older version could still be the legacy 7-element array of
 * float HOURS. Both are accepted; anything else falls back to a zero week, which
 * shows as "no target hours configured" rather than silently inventing a target.
 */
export function parseHoursPerDay(value: unknown): HoursPerDay {
  const zero: HoursPerDay = { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 };

  if (Array.isArray(value) && value.length === 7) {
    // Legacy shape: hours, not seconds, indexed 0=Monday.
    return WEEKDAY_KEYS.reduce((acc, key, i) => {
      const hours = Number(value[i]);
      acc[key] = Number.isFinite(hours) ? Math.round(hours * 3600) : 0;
      return acc;
    }, { ...zero });
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return WEEKDAY_KEYS.reduce((acc, key) => {
      const seconds = Number(record[key]);
      acc[key] = Number.isFinite(seconds) && seconds >= 0 ? Math.round(seconds) : 0;
      return acc;
    }, { ...zero });
  }

  return zero;
}
